import { useEffect, useState } from 'preact/hooks'
import { integrationsApi } from '@/api/integrations'
import { useAuth } from '@/auth/useAuth'
import { holdsCompanyAdminRole } from '@/components/console/admin/useCompanyAdminAccess'
import { findBrokenCrm } from '@/components/settings/integrations/crmSlot'
import type { IntegrationConnectionViewDto } from '@/types/integrations'

/**
 * The company's CRM connection when it is broken and a company admin is looking, otherwise null
 * (ORB-966).
 *
 * Company admins only, on both halves: the list it reads is [RequireCustomerAccess], so anyone else
 * would get a 403, and the fix it points to (Settings → Integrations) is admin-only too. Everyone else
 * never makes the request.
 */
export function useCrmConnectionAlert(clientCompanyId: string | undefined): IntegrationConnectionViewDto | null {
  const { userCompanyRoleSets } = useAuth()
  const isCompanyAdmin = !!clientCompanyId && holdsCompanyAdminRole(userCompanyRoleSets, clientCompanyId)
  const [brokenCrm, setBrokenCrm] = useState<IntegrationConnectionViewDto | null>(null)

  useEffect(() => {
    setBrokenCrm(null)
    if (!isCompanyAdmin || !clientCompanyId) return

    let mounted = true
    integrationsApi.getIntegrationConnections(clientCompanyId)
      .then(response => {
        if (mounted && response.data) setBrokenCrm(findBrokenCrm(response.data))
      })
      .catch(error => console.warn('Failed to check CRM connection health:', error))
    return () => { mounted = false }
  }, [clientCompanyId, isCompanyAdmin])

  return brokenCrm
}
